import React, { useEffect, useState } from 'react';
import { injectIntl } from "react-intl";
import axios from 'axios';
import { Formik, Field, Form } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  IconButton,
  TextField,
  Typography,
  Select,
  MenuItem,
} from '@material-ui/core';
import { Create } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import messages from './messages'
import moment from 'moment-timezone';
import styles from '../styles';
import Panel from '../../../common/elements/Panel';
import PassengerTypeComponent from './PassengerTypeComponent';
import LocationSearch from './LocationSearch';
import CustomerSearch from './CustomerSearch';
import DateTimePicker from './DateTimePicker';
import { validateRequired } from '../../../common/validation/validation';
import useGlobalContext from '../../../common/hooks/useGlobalContext';
import BusinessDropdown from '../../../common/elements/BusinessDropdown';
import ServiceDropdown from '../../../common/elements/ServiceDropdown';

const validateForm = values => {
  let errors = {};
  const validationResults = {
    phone_number: validateRequired(values.phone_number),
    rider_name: validateRequired(values.rider_name),
    start_location: validateRequired(values.start_location),
    end_location: validateRequired(values.end_location),
    expected_duration:
      values.expected_duration && !values.scheduled_for_pickup_at
        ? 'Package rides must be scheduled'
        : false,
  };
  if (Object.values(validationResults).some(value => Boolean(value))) {
    errors = validationResults;
  }
  return errors;
};

const panelTitle = 'Create Ride';

export const NewRidePanel = props => {
  const {
    classes,
    fetchRides,
    fetchScheduledTrips,
    intl: { formatMessage, formatNumber },
    passengerTypes,
  } = props;

  const {
    state: { activeServiceId, activeBusinessId, services, activeService, tenantSettings },
    setError,
    setLoading,
  } = useGlobalContext();
  const [pricingBlocks, setPricingBlocks] = useState([]);
  const [enablePackageSelection, setEnablePackageSelection] = useState(false);
  const [selectedTimeZoneData, setSelectedTimeZoneData] = useState();

  const fetchAndResetPackages = (serviceId, setFieldValue) => {
    setFieldValue && setFieldValue('expected_duration', 0);
    axios.get(`/api/v2/ridesharingservices/${serviceId}`).then(({ data }) => {
      const { scheduled_trip_enable_block_pricing, pricing, service_location, time_zone } = data;
      setEnablePackageSelection(scheduled_trip_enable_block_pricing);
      setPricingBlocks(pricing && pricing.pricing_blocks);
      setSelectedTimeZoneData({ time_zone, service_location });
    });
  };

  useEffect(() => {
    fetchAndResetPackages(activeServiceId);
  }, [activeService, activeServiceId]);

  const displayPackageDuration = (pkg, currencyCode) => {
    const { hours, minutes } = moment.duration(pkg.block_duration, 'seconds')._data;
    return `${hours}hr${minutes > 0 ? ` ${minutes}min` : ''} - ${formatNumber(pkg.price, {
      currency: currencyCode,
      style: 'currency',
    })}`;
  };

  const initialShouldEstimateFare = {
    startLocation: '',
    endLocation: '',
    rideTime: '',
    serviceId: activeServiceId,
  };

  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const [shouldEstimateFare, setShouldEstimateFare] = useState(initialShouldEstimateFare);
  const [showEstimate, setShowEstimate] = useState(false);
  const [estimatedFare, setEstimatedFare] = useState();

  const [availableDays, setAvailableDays] = useState([]);

  useEffect(() => {
    const fetchAvailableDays = () =>
      axios
        .get(
          `/api/v2/ridesharingservices/${activeServiceId}/schedule_availability`
        )
        .then(({ data }) => setAvailableDays(data));
    if (activeServiceId) {
      fetchAvailableDays();
    }
  }, [activeServiceId]);

  useEffect(() => {
    const estimateFare = ({ startLocation, endLocation, rideTime, serviceId }) => {
      setEstimatedFare();
      if (startLocation && endLocation) {
        if (!showEstimate) {
          setShowEstimate(true);
        }
        const params = {
          end_lat: endLocation.lat,
          end_lng: endLocation.lng,
          scheduled_for_pickup_at: rideTime ? rideTime.format() : moment().format(),
          service_id: serviceId,
          start_lat: startLocation.lat,
          start_lng: startLocation.lng,
        };
        axios
          .get('/api/v2/fare_estimates/', { params })
          .then(({ data: { amount_display } }) => setEstimatedFare(amount_display));
      } else {
        if (showEstimate) {
          setShowEstimate(false);
        }
      }
    };
    estimateFare(shouldEstimateFare);
  }, [shouldEstimateFare, showEstimate, activeServiceId]);

  const initialPassengers = passengerTypes
    ? passengerTypes.map(({ id, label, default: isDefault, mandatory }) => {
        if (isDefault || mandatory) {
          return {
            passenger_type_id: id,
            label,
            quantity: 1,
            mandatory,
          };
        } else {
          return {
            passenger_type_id: id,
            label,
            quantity: 0,
            mandatory,
          };
        }
      })
    : [{ passenger_type_id: 1, label: 'Rider', quantity: 1, mandatory: true }];

  const initialValues = {
    app_id: 9,
    bike_rack_requested: false,
    business_entity_id: activeBusinessId ? activeBusinessId : '',
    rider_comments: '',
    end_location: '',
    lift_requested: false,
    passengers: initialPassengers,
    payment_mode: 'deferred',
    phone_number: '',
    rider_name: '',
    ride_time: 'now',
    scheduled_for_pickup_at: '',
    service_id: activeServiceId ? activeServiceId : '',
    start_location: '',
    expected_duration: 0,
  };

  const resetLocationField = (values, setFieldValue) => {
    if (values.start_location !== initialValues.start_location) {
      setFieldValue('start_location', initialValues.start_location);
    }
    if (values.end_location !== initialValues.end_location) {
      setFieldValue('end_location', initialValues.end_location);
    }
  };

  const resetCustomersField = (values, setFieldValue) => {
    if (values.phone_number !== initialValues.phone_number) {
      setFieldValue('phone_number', initialValues.phone_number);
    }
    if (values.rider_name !== initialValues.rider_name) {
      setFieldValue('rider_name', initialValues.rider_name);
    }
  };

  const clearFareEstimate = () => {
    setShowEstimate(false);
    setShouldEstimateFare(initialShouldEstimateFare);
    setEstimatedFare();
  };

  const createRide = (values, { resetForm, setSubmitting }) => {
    const { ride_time, ...modifiedValues } = values;
    if (ride_time === 'now') {
      const { scheduled_for_pickup_at, ...modifiedValues } = values;
      if (!values.expected_duration) modifiedValues.expected_duration = null;
      axios
        .post('/api/v2/trips', modifiedValues)
        .then(() => {
          setLoading(true);
          clearFareEstimate();
          fetchRides().finally(() => setLoading(false));
          resetForm(initialValues);
        })
        .catch(e => {
          setError(e);
          setSubmitting(false);
        });
    } else {
      modifiedValues.scheduled_for_pickup_at = values.scheduled_for_pickup_at.format();
      if (!values.expected_duration) modifiedValues.expected_duration = null;
      axios
        .post('/api/v2/scheduledtriprequests', modifiedValues)
        .then(() => {
          setLoading(true);
          clearFareEstimate();
          fetchScheduledTrips().finally(() => setLoading(false));
          resetForm(initialValues);
        })
        .catch(e => {
          setError(e);
          setSubmitting(false);
        });
    }
  };

  const onPackageRideSelection = (option, setFieldValue, values) => {
    const {
      end_location: endLocation,
      scheduled_for_pickup_at: rideTime,
      service_id: serviceId,
      start_location: startLocation,
    } = values;
    const blockValue = (option.props && option.props.value) || 0;
    setFieldValue('expected_duration', blockValue);
    blockValue ? setFieldValue('ride_time', 'later') : setFieldValue('ride_time', 'now');
    blockValue
      ? clearFareEstimate()
      : setShouldEstimateFare({ endLocation, rideTime, serviceId, startLocation });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={createRide}
      validateOnChange={false}
      validateOnBlur={false}
      validate={validateForm}
    >
      {formikProps => {
        const {
          errors,
          handleChange,
          isSubmitting,
          resetForm,
          setFieldValue,
          values,
        } = formikProps;

        return (
          <Form>
            <Panel
              title={panelTitle}
              renderFooter={() => (
                <Box>
                  <Divider />
                  {showEstimate && (
                    <Box mt={2}>
                      <Typography display="inline">
                        <b>{formatMessage(messages.fareEstimate)}:</b>
                      </Typography>
                      <Box display="inline" ml={2}>
                        {estimatedFare ? (
                          <Typography display="inline">{estimatedFare}</Typography>
                        ) : (
                          <CircularProgress color="primary" size={15} />
                        )}
                      </Box>
                    </Box>
                  )}
                  <Box mt={2}>
                    <Button
                      color="primary"
                      onClick={() => {
                        clearFareEstimate();
                        resetForm(initialValues);
                      }}
                      variant="outlined"
                    >
                      {formatMessage(messages.clear)}
                    </Button>
                    <Box ml={2} display="inline">
                      <Button
                        color="primary"
                        className={classes.createRideButton}
                        type="submit"
                        disabled={isSubmitting}
                        variant="contained"
                      >
                        {formatMessage(messages.createRide)}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            >
              <InputLabel>
                <strong>{formatMessage(messages.business)}</strong>
              </InputLabel>
              <Field name="business_entity_id">
                {({ field }) => (
                  <Box mt={1}>
                    <FormControl fullWidth>
                      <BusinessDropdown
                        {...field}
                        onChange={e => {
                          handleChange(e);
                          resetCustomersField(values, setFieldValue);
                        }}
                      />
                    </FormControl>
                  </Box>
                )}
              </Field>
              {services.length > 1 && (
                <Box mt={2}>
                  <InputLabel>
                    <strong>{formatMessage(messages.service)}</strong>
                  </InputLabel>
                  <Field name="service_id">
                    {({ field }) => (
                      <Box mt={1}>
                        <FormControl fullWidth>
                          <ServiceDropdown
                            {...field}
                            onChange={e => {
                              const serviceId = e.target.value;
                              handleChange(e);
                              fetchAndResetPackages(serviceId, setFieldValue);
                              resetLocationField(values, setFieldValue);
                              setShouldEstimateFare({
                                ...initialShouldEstimateFare,
                                serviceId,
                              });
                              setFieldValue('ride_time', 'now');
                              setFieldValue('scheduled_for_pickup_at', '');
                            }}
                          />
                        </FormControl>
                      </Box>
                    )}
                  </Field>
                </Box>
              )}
              <Box mt={3}>
                <InputLabel>
                  <strong>{formatMessage(messages.rider)}</strong>
                </InputLabel>
                <Box mt={1}>
                  <CustomerSearch
                    display="phone"
                    formikProps={formikProps}
                    name="phone_number"
                    label={formatMessage(messages.phoneNumber)}
                    onChange={(_, v) => {
                      setFieldValue(
                        'phone_number',
                        v ? v.user.phone_number : initialValues.phone_number
                      );
                      setFieldValue('rider_name', v ? v.full_name : values.rider_name);
                    }}
                    onFocus={() =>
                      values.phone_number.length < 2 && setFieldValue('phone_number', '+1')
                    }
                  />
                </Box>
                <Box mt={2}>
                  <CustomerSearch
                    display="name"
                    formikProps={formikProps}
                    name="rider_name"
                    label={formatMessage(messages.employeeName)}
                    onChange={(_, v) => {
                      setFieldValue('rider_name', v ? v.full_name : initialValues.rider_name);
                      setFieldValue('phone_number', v ? v.user.phone_number : values.phone_number);
                    }}
                  />
                </Box>
                <Box mt={2}>
                  <LocationSearch
                    formikProps={formikProps}
                    initialValues={initialValues}
                    name="start_location"
                    onChange={v =>
                      !values.expected_duration &&
                      setShouldEstimateFare({ ...shouldEstimateFare, startLocation: v })
                    }
                    label={formatMessage(messages.startAddress)}
                  />
                </Box>
                <Box mt={2}>
                  <LocationSearch
                    formikProps={formikProps}
                    initialValues={initialValues}
                    name="end_location"
                    onChange={v =>
                      !values.expected_duration &&
                      setShouldEstimateFare({ ...shouldEstimateFare, endLocation: v })
                    }
                    label={formatMessage(messages.endAddress)}
                  />
                </Box>
              </Box>
              {enablePackageSelection && pricingBlocks && (
                <Box mt={2}>
                  <FormControl fullWidth>
                    <Select
                      onChange={(_, option) =>
                        onPackageRideSelection(option, setFieldValue, values)
                      }
                      value={values.expected_duration}
                      variant="outlined"
                    >
                      <MenuItem key={0} value={0}>
                        {formatMessage(messages.selectPackageType)}
                      </MenuItem>
                      {pricingBlocks.map(pkg => (
                        <MenuItem key={pkg.id} value={pkg.block_duration}>
                          {displayPackageDuration(pkg, tenantSettings.currency_code)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box mt={3}>
                <InputLabel>
                  <strong>{formatMessage(messages.numberOfPassengers)}</strong>
                </InputLabel>
                <Box mt={2}>
                  {passengerTypes ? (
                    passengerTypes.map(({ id, label }) => (
                      <PassengerTypeComponent
                        key={id}
                        label={label}
                        id={id}
                        passengers={values.passengers}
                        setFieldValue={setFieldValue}
                      />
                    ))
                  ) : (
                    <PassengerTypeComponent
                      key={1}
                      label={formatMessage(messages.rider)}
                      id={1}
                      passengers={values.passengers}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </Box>
              </Box>
              <Box mt={2}>
                <InputLabel>
                  <strong>{formatMessage(messages.extras)}</strong>
                </InputLabel>
                <Box mt={1} ml={4}>
                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          name="bike_rack_requested"
                          checked={values.bike_rack_requested}
                          onChange={handleChange}
                        />
                      }
                      label={formatMessage(messages.bike)}
                    />
                  </div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        name="lift_requested"
                        checked={values.lift_requested}
                        onChange={handleChange}
                      />
                    }
                    label={formatMessage(messages.wheelchairSpace)}
                  />
                </Box>
              </Box>
              <Box mt={3}>
                <InputLabel>
                  <strong>{formatMessage(messages.time)}</strong>
                </InputLabel>
                <Box mt={1} ml={4} style={{ display: 'flex' }}>
                  <RadioGroup
                    aria-label="gender"
                    name="ride_time"
                    onChange={handleChange}
                    row
                    value={values.ride_time}
                  >
                    {values.expected_duration ? (
                      <React.Fragment>
                        <Box>
                          <Button
                            color="primary"
                            onClick={() => setDatePickerOpen(true)}
                            variant="outlined"
                          >
                            {formatMessage(messages.scheduleRide)}
                          </Button>
                          {errors.expected_duration && !values.scheduled_for_pickup_at && (
                            <Typography className={classes.errorMessage}>
                              {formatMessage(messages.packageRideNotScheduled)}
                            </Typography>
                          )}
                        </Box>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <FormControlLabel
                          value="now"
                          control={
                            <Radio
                              color="primary"
                              onClick={() => {
                                if (values.scheduled_for_pickup_at) {
                                  setFieldValue('scheduled_for_pickup_at', '');
                                  setShouldEstimateFare({ ...shouldEstimateFare, rideTime: '' });
                                }
                              }}
                            />
                          }
                          label={formatMessage(messages.now)}
                        />
                        <FormControlLabel
                          value="later"
                          control={
                            <Radio color="primary" onClick={() => setDatePickerOpen(true)} />
                          }
                          label={formatMessage(messages.later)}
                        />
                      </React.Fragment>
                    )}
                  </RadioGroup>
                  {values.scheduled_for_pickup_at && (
                    <Box
                      ml={3}
                      style={{
                        display: 'flex',
                      }}
                    >
                      <Box mt={1.8} width="190px">
                        <Typography variant="subtitle2">
                          {values.scheduled_for_pickup_at.format('MMMM Do, h:mm a')}
                        </Typography>
                      </Box>
                      <IconButton onClick={() => setDatePickerOpen(true)}>
                        <Create />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box mt={1} mb={3}>
                <Field name="rider_comments">
                  {({ field }) => <TextField {...field} fullWidth label={formatMessage(messages.comments)} />}
                </Field>
              </Box>
              {selectedTimeZoneData && (
                <DateTimePicker
                  availableDays={availableDays}
                  closePicker={() => setDatePickerOpen(false)}
                  formikProps={formikProps}
                  initialValue={values.scheduled_for_pickup_at}
                  onConfirm={v => setShouldEstimateFare({ ...shouldEstimateFare, rideTime: v })}
                  open={datePickerOpen}
                  name="scheduled_for_pickup_at"
                  selectedTimeZoneData={selectedTimeZoneData}
                />
              )}
            </Panel>
          </Form>
        );
      }}
    </Formik>
  );
};

export default withStyles(styles)(injectIntl(NewRidePanel));
